import { useCallback } from 'react'

export type UserInfo = {
  email: string
  firstName: string
  lastName: string
  middleName: string
  phoneNumber: string
  stateCode: string
}

const sessionStorageKey = 'uma:userToken'
const secretKey = process.env.GATSBY_ENCRYPTED_FIRST_NAME || ''

type UseUserInfoAPI = {
  /**
   * Removes the user's info from session storage.
   */
  clearUserInfo: () => void

  /**
   * Grabs the user's info from session storage and returns a decrypted copy.
   * Returns `null` if the user's info isn't in session storage.
   */

  getUserInfo: () => Promise<UserInfo | null>

  /**
   * Saves an encrypted copy of the user's info in session storage.
   */
  setUserInfo: (userInfo: UserInfo) => void
}

function useUserInfo(): UseUserInfoAPI {
  if (!secretKey) {
    // eslint-disable-next-line no-console
    console.error('The GATSBY_ENCRYPTED_FIRST_NAME environment variable is not set.')
  }

  const setUserInfo = useCallback(async (userInfo: UserInfo) => {
    const result = await fetch('/.netlify/functions/self-enroll-cipher', {
      body: JSON.stringify(userInfo),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })
    const json = await result.json()
    sessionStorage.setItem(
      sessionStorageKey,
      JSON.stringify({ iv: json.body.iv, token: json.body.token as unknown as string }),
    )
  }, [])

  const getUserInfo = useCallback(async () => {
    const query = new URLSearchParams(window.location.search)
    // try to grab from query string or session storage
    let selfEnrollQuery = { iv: query.get('seiv'), token: query.get('se') }
    if (!selfEnrollQuery.iv || !selfEnrollQuery.token) {
      selfEnrollQuery = JSON.parse(sessionStorage.getItem(sessionStorageKey))
    }
    // decrypt if present
    if (selfEnrollQuery && selfEnrollQuery.iv && selfEnrollQuery.token) {
      const result = await fetch('/.netlify/functions/self-enroll-decipher', {
        body: JSON.stringify(selfEnrollQuery),
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      })
      const json = await result.json()
      return JSON.parse(json.body.deciphered) as UserInfo
    }
    return Promise.resolve(null)
  }, [])

  const clearUserInfo = useCallback(() => {
    sessionStorage.removeItem(sessionStorageKey)
  }, [])

  return {
    clearUserInfo,
    getUserInfo,
    setUserInfo,
  }
}

export default useUserInfo
